import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton} from '@mui/material';
import {Article} from '../../api/caseApiTypes';
import {CaseArticles} from '../cases/CaseArticles';
import {CaseField} from '../cases/layoutComponents/CaseField';
import React, {useState} from 'react';
import {OutlinedLabel} from '../../helpers/OutlinedLabel';
import {Delete, Info, CheckBox, CheckBoxOutlineBlank, IndeterminateCheckBox, Edit, Close} from "@mui/icons-material";
import styles from './ImageArticles.module.css';

export type ArticleChange = (articles: Article[]) => Article[];

interface Props {
  articles: Article[];
  maxArticles: number | undefined;
  noun: string;
  onChange: (change: ArticleChange) => void;
  readonly?: boolean;
}

export const ImageArticles: React.FC<Props> = ({articles, maxArticles, noun, onChange, readonly}) => {

  const [selectEditMode, setSelectEditMode] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isOpenInfo, setIsOpenInfo] = useState<boolean>(false);
  const [openInfoIds, setOpenInfoIds] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const toggleSelect = (id?: string) => {
    if (!id) return;

    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id],
    );

    if (isOpenInfo) {
      setOpenInfoIds(prev =>
        prev.includes(id)
          ? prev.filter(x => x !== id)
          : [...prev, id],
      );
    }

    if (selectedIds.length === 0) {
      setOpenInfoIds([]);
      setIsOpenInfo(false);
    }
  };

  const toggleOpenInfo = (id?: string) => {
    if (!id) return;

    setOpenInfoIds(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id],
    );
  }

  const toggleOpenInfos = () => {
    setIsOpenInfo(prev => {
      const next = !prev;

      if (next) {
        setOpenInfoIds([...selectedIds]);
      } else {
        setOpenInfoIds([]);
      }

      return next;
    });
  };

  const selectAll = () => {
    const allIds = articles
      .map(a => a.id)
      .filter((id): id is string => id !== undefined);

    if (selectedIds.length === allIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allIds);
    }
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setOpenInfoIds([]);
  };

  const deleteSelected = () => {
    setConfirmOpen(true);
  };

  const confirmDelete = () => {
    onChange(old => old.filter(a => !selectedIds.includes(a.id!)));
    clearSelection();
    setConfirmOpen(false);
  };

  const showBulkInfo = () => {
    setOpenInfoIds([...selectedIds]);
  };

  const onDeleteArticle = (article: Article) => {
    onChange(oldArticles => oldArticles.filter(other => other.articleId !== article.articleId));
  };

  const onUpsertArticle = (updated: Article) => onUpsertManyArticles([updated])
  const onUpsertManyArticles = (updates: Article[]) => {
    onChange(oldArticles => {
      const newArticles = oldArticles.slice();
      for (let updated of updates) {
        const existingIndex = oldArticles.findIndex(article => article.id === updated.id);
        if (existingIndex < 0) {
          newArticles.push(updated);
        } else {
          newArticles.splice(existingIndex, 1, updated);
        }
      }
      return newArticles;
    });
  };

  return (
    <>
      <CaseField label={noun}>
        {!readonly && (
          <Box className={styles.selectWrapper}>
            {!selectEditMode ? (
                <IconButton
                  size="small"
                  onClick={() => setSelectEditMode(true)}
                >
                  <Edit/>
                </IconButton>
              ) :
              (
                <OutlinedLabel label="Markerade artiklar">
                  <Box sx={{mb: 2}} className={styles.selectCheckWrapper}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedIds([]);
                        setSelectEditMode(false);
                      }}
                    >
                      <Close/>
                    </IconButton>

                    <IconButton
                      onClick={selectAll}
                    >
                      {
                        selectedIds.length === 0 ? (
                          <CheckBoxOutlineBlank/>
                        ) : selectedIds.length === articles.length ? (
                          <CheckBox/>
                        ) : (
                          <IndeterminateCheckBox/>
                        )
                      }
                    </IconButton>

                    <Box className={styles.selectActionWrapper}>

                      <Box sx={{ paddingRight: '10px' }}
                           className={styles.selectActionSelected}>
                        {selectedIds.length === articles.length
                          ? "Avmarkera alla"
                          : "Markera alla"}
                      </Box>

                      <Box sx={{ paddingRight: '10px' }}
                           className={styles.selectActionSelected}>
                        |
                      </Box>

                      <Box className={styles.selectActionSelected}>{selectedIds.length} markerade</Box>

                      <IconButton
                        disabled={selectedIds.length === 0}
                        onClick={toggleOpenInfos}
                      >
                        <Info/>
                      </IconButton>

                      <IconButton
                        disabled={selectedIds.length === 0}
                        onClick={deleteSelected}
                      >
                        <Delete/>
                      </IconButton>
                    </Box>
                  </Box>
                </OutlinedLabel>
              )}
          </Box>
        )}

        <CaseArticles
          articles={articles}
          onUpsert={onUpsertArticle}
          onAddMany={onUpsertManyArticles}
          onDelete={onDeleteArticle}
          readonly={readonly}
          selectEditMode={selectEditMode}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          renderDeliveryStatus={false}
          maxArticles={maxArticles}
          pluralVerb={noun}
          openInfoIds={openInfoIds}
          onToggleOpenInfo={toggleOpenInfo}
          enableDrag
        />
      </CaseField>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Ta bort artiklar?</DialogTitle>
        <DialogContent>
          {selectedIds.length} artiklar kommer att tas bort. Vill du fortsätta?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            Avbryt
          </Button>
          <Button color="error" onClick={confirmDelete}>
            Ta bort
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
